import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type LowTideSuggestionsStateWire } from '../lib/api-client';

const LOW_TIDE_SUGGESTIONS_KEY = ['low-tide-suggestions'] as const;

/**
 * useLowTideSuggestions — S5 Corrente de Compromissos (maré baixa produtiva,
 * Item 405, `GET /v1/agent-dashboard/low-tide-suggestions`).
 *
 * `enabled` GATEIA O FETCH (não só o render) — o caller (`PainelV3`) passa
 * `hasCap = useCapability('proactive-work-suggestions')`. Cap opt-in.
 */
export function useLowTideSuggestions(
  enabled: boolean,
): ReturnType<typeof useQuery<LowTideSuggestionsStateWire, Error>> {
  return useQuery({
    queryKey: LOW_TIDE_SUGGESTIONS_KEY,
    queryFn: () => api.getLowTideSuggestions(),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * useDismissLowTideSuggestion — «agora não» no card (invalida a lista — a
 * sugestão dispensada some; o backend já grava o audit que suprime 48h).
 */
export function useDismissLowTideSuggestion(): ReturnType<
  typeof useMutation<void, Error, { id: string; personId: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, personId }) => api.dismissLowTideSuggestion(id, personId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LOW_TIDE_SUGGESTIONS_KEY });
    },
  });
}

/**
 * useAcceptLowTideSuggestion — audit de aceite («fazer agora»/«delegar à
 * IA»/«ver pessoa»). Fire-and-forget do ponto de vista da navegação (o
 * gesto em si — abrir conversa/pessoa/delegar — acontece em paralelo no
 * `PainelV3`; esta mutation só registra a fonte autoritativa do aceite).
 */
export function useAcceptLowTideSuggestion(): ReturnType<
  typeof useMutation<
    void,
    Error,
    { id: string; personId: string; gesture: 'fazer-agora' | 'ver-pessoa' | 'delegar' }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, personId, gesture }) => api.acceptLowTideSuggestion(id, personId, gesture),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LOW_TIDE_SUGGESTIONS_KEY });
    },
  });
}
